import { useEffect, useState } from "react";
import { TrendingUp, Award, Flame, Repeat, Target, Loader2, Trophy, Lock, Medal } from "lucide-react";
import { api } from "@/lib/api";

const LEVEL_STYLE = {
  "Débutant": "bg-white/15 text-white",
  "En progression": "bg-[#C89A3E]/25 text-[#F5C64E]",
  "Confirmé": "bg-blue-400/20 text-blue-100",
  "Expert": "bg-[#F5C64E] text-[#012141]",
};

const Bar = ({ label, value, target, suffix = "" }) => {
  const pct = Math.min(100, Math.round((value / target) * 100));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1"><span className="text-slate-500 font-semibold">{label}</span><span className="text-[#012141] font-bold">{value}{suffix} / {target}{suffix}</span></div>
      <div className="h-2 rounded-full bg-[#012141]/10 overflow-hidden"><div className="h-full rounded-full bg-[#C89A3E] transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const Challenge = ({ c }) => (
  <div data-testid="coach-challenge" className={`rounded-2xl border p-5 ${c.done ? "border-[#C89A3E] bg-[#C89A3E]/10" : "border-[#012141]/10 bg-white"}`}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2 text-sm font-bold text-[#012141]"><Trophy size={16} className="text-[#C89A3E]" /> Défi de la semaine · Niveau {c.level ?? 1}</div>
      <span data-testid="coach-challenge-badge" className={`inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full ${c.done ? "bg-[#F5C64E] text-[#012141]" : "bg-slate-100 text-slate-500"}`}>
        {c.done ? "🎉 Débloqué" : <><Lock size={11} /> À débloquer</>}
      </span>
    </div>
    <div className="space-y-2.5">
      <Bar label="Sessions" value={c.week_sessions} target={c.target_sessions} />
      <Bar label="Moyenne" value={c.week_avg ?? 0} target={c.target_avg} suffix="/10" />
    </div>
    <p className="mt-3 text-xs text-slate-500">{c.done ? "Bravo — objectif atteint ! Badge de la semaine débloqué." : `Objectif : ${c.target_sessions} sessions à ${c.target_avg}/10 de moyenne cette semaine.`}</p>
  </div>
);

const Stat = ({ icon: Icon, label, value, suffix }) => (
  <div className="rounded-2xl border border-[#012141]/10 bg-white p-4">
    <div className="flex items-center gap-2 text-slate-400 text-xs font-semibold uppercase tracking-wide"><Icon size={14} className="text-[#C89A3E]" /> {label}</div>
    <div className="mt-1 text-2xl font-bold text-[#012141] font-heading">{value ?? "—"}<span className="text-sm text-slate-400 font-semibold">{value != null ? suffix : ""}</span></div>
  </div>
);

export const CoachProgress = ({ compact = false }) => {
  const [p, setP] = useState(null);
  useEffect(() => { api.coachProgress().then(setP).catch(() => setP({ count: 0 })); }, []);

  if (!p) return <div className="text-slate-400 text-sm py-8 text-center"><Loader2 className="animate-spin inline mr-2" size={15} />Chargement…</div>;

  if (!p.count) {
    return (
      <div data-testid="coach-progress-empty" className="rounded-2xl border border-dashed border-[#012141]/20 bg-white p-8 text-center">
        <div className="grid place-items-center w-14 h-14 rounded-2xl bg-[#012141] text-[#F5C64E] mx-auto"><TrendingUp size={26} /></div>
        <h3 className="mt-4 font-heading font-bold text-[#012141] text-lg">Votre progression commence ici</h3>
        <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Terminez une session d'entraînement vocal avec bilan pour voir votre note, votre courbe d'amélioration et débloquer vos niveaux.</p>
      </div>
    );
  }

  return (
    <div data-testid="coach-progress" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-2xl navy-surface-soft p-5 text-white">
        <div>
          <div className="text-xs uppercase tracking-wider text-[#F5C64E] font-bold">Votre niveau</div>
          <div className="mt-1 font-heading text-2xl font-bold">{p.level}</div>
          <div className="mt-1 text-sm text-white/70">{p.count} session(s) complétée(s)</div>
        </div>
        <span data-testid="coach-level-badge" className={`text-sm font-bold px-4 py-2 rounded-full ${LEVEL_STYLE[p.level] || "bg-white/10 text-white"}`}>{p.level}</span>
      </div>

      {p.challenge && <Challenge c={p.challenge} />}

      {p.badges?.length > 0 && (
        <div data-testid="coach-badges" className="rounded-2xl border border-[#012141]/10 bg-white p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><Medal size={14} className="text-[#C89A3E]" /> Badges gagnés ({p.badges.length})</div>
          <div className="flex flex-wrap gap-2">
            {(compact ? p.badges.slice(-6) : p.badges).map((b, i) => (
              <div key={i} title={`Semaine ${b.week} · ${b.target_sessions} sessions à ${b.target_avg}/10`} className="flex items-center gap-1.5 rounded-full bg-[#012141] text-[#F5C64E] px-3 py-1.5 text-xs font-bold">
                <Medal size={13} /> Niv. {b.level}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={Target} label="Moyenne" value={p.average} suffix="/10" />
        <Stat icon={Award} label="Meilleur" value={p.best} suffix="/10" />
        <Stat icon={Flame} label="Dernier" value={p.last} suffix="/10" />
        <Stat icon={Repeat} label="Sessions" value={p.count} suffix="" />
      </div>

      {p.trend?.length > 1 && (
        <div className="rounded-2xl border border-[#012141]/10 bg-white p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3 flex items-center gap-1.5"><TrendingUp size={14} className="text-[#C89A3E]" /> Courbe d'amélioration</div>
          <div className="flex items-end gap-1.5 h-28">
            {p.trend.map((t, i) => (
              <div key={i} className="flex-1 flex flex-col items-center justify-end group">
                <div className="w-full rounded-t-md bg-[#012141] group-hover:bg-[#C89A3E] transition-colors" style={{ height: `${Math.max(6, (t.note / 10) * 100)}%` }} title={`${t.note}/10 — ${t.date}`} />
                <span className="mt-1 text-[9px] text-slate-400">{t.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!compact && p.recent?.length > 0 && (
        <div className="rounded-2xl border border-[#012141]/10 bg-white p-5">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Sessions récentes</div>
          <div className="space-y-1">
            {p.recent.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-[#012141]/5 last:border-0">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#012141] truncate">{r.persona || "Client virtuel"}</div>
                  <div className="text-xs text-slate-400">{r.difficulty || ""} · {r.date}</div>
                </div>
                <span className="text-sm font-bold text-[#012141]">{r.note != null ? `${r.note}/10` : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
